import { DeleteOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Form,
  Row,
  Col,
  message,
  Select,
  Flex,
  DatePicker,
  Breadcrumb,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { mutationOnErrorHandler } from "../../../utils/mutationUtils";
import { useEffect } from "react";
import {
  approveOrderRequest,
  cancelOrderRequest,
  getOrderByIdRequest,
  revokeOrderRequest,
  updateOrderRequest,
} from "../../../api/requests/order";
import { useContacts } from "../../../api/hooks/customer";
import { useProducts } from "../../../api/hooks/product";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import OrderShippingDetails from "./OrderShippingDetails";
import OrderAssetDetails from "./OrderAssetDetails";
import { useCurrentUser } from "../../../api/hooks/auth";
import OrderReceiverAddress from "./OrderReceiverAddress";
import {
  BIN_WIEGHT,
  BINS_COUNT_PER_PALLET,
  PALLET_WIEGHT,
} from "../../../constants";
import { useSettings } from "../../../api/hooks";

const updateOrderSchemaResolver = yupResolver(
  yup.object().shape({
    carrier: yup.string(),
    customer: yup.string().required("Customer is required"),
    requiredDate: yup.date().required("Delivery Date is required"),
    items: yup
      .array()
      .of(
        yup.object().shape({
          product: yup.string().required("Product is required"),
          requiredQuantity: yup
            .number()
            .required("Required Quantity is required")
            .positive("Required Quantity must be a positive number")
            .integer("Required Quantity must be an integer"),
        }),
      )
      .min(1, "Items must contain at least one item")
      .required("Items are required"),
    receiverAddress: yup.object().shape({
      name: yup.string(),
      address: yup.object().shape({
        street: yup.string(),
        city: yup.string(),
        state: yup.string(),
        zipCode: yup.string(),
        country: yup.string(),
      }),
    }),
  }),
);

const initialItemDetail = {
  product: undefined,
  requiredQuantity: 0,
};

function UpdateOrderPage() {
  const params = useParams();
  const { id } = params;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: settingDetails } = useSettings();

  const { data: userData } = useCurrentUser();

  const { data: carrierList, isLoading: isLoadingCarrierList } = useContacts({
    params: {
      type: "CARRIER",
    },
  });
  const { data: customerList, isLoading: isLoadingCustomerList } = useContacts({
    params: {
      type: ["CUSTOMER", "PROCESSOR"],
    },
  });
  const { data: productList, isLoading: isLoadingProductList } = useProducts({
    params: {},
  });

  const { data: orderDetails, isLoading: isLoadingOrderDetails } = useQuery({
    queryKey: ["order", id],
    queryFn: async () => {
      const res = await getOrderByIdRequest({
        id,
        params: {},
      });
      return res.data?.data;
    },
  });

  const { mutateAsync: updateOrder, isPending: updatingOrder } = useMutation({
    mutationFn: async (data) => {
      const res = await updateOrderRequest({ id, data });
      return res.data;
    },
    mutationKey: ["order", id],
    onSuccess: (res) => {
      queryClient.invalidateQueries(["order"]);
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
      navigate(-1);
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  const { mutateAsync: approveOrder, isPending: approvingOrder } = useMutation({
    mutationFn: async (data) => {
      const res = await approveOrderRequest({ orderId: id, data });
      return res.data;
    },
    mutationKey: [`order/${id}/approve`],
    onSuccess: (res) => {
      queryClient.invalidateQueries(["order", id]);
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  const { mutateAsync: cancelOrder, isPending: cancelingOrder } = useMutation({
    mutationFn: async (data) => {
      const res = await cancelOrderRequest({ orderId: id, data });
      return res.data;
    },
    mutationKey: [`order/${id}/cancel`],
    onSuccess: (res) => {
      queryClient.invalidateQueries(["order", id]);
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  const { mutateAsync: revokeOrder, isPending: revokingOrder } = useMutation({
    mutationFn: async (data) => {
      const res = await revokeOrderRequest({ orderId: id, data });
      return res.data;
    },
    mutationKey: [`order/${id}/approve-revoke`],
    onSuccess: (res) => {
      queryClient.invalidateQueries(["order", id]);
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: updateOrderSchemaResolver,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  // console.log("errors----->", errors);
  const items = watch("items");
  const binCount = items?.[0]?.requiredQuantity || 0;

  async function onSubmit(data) {
    if (!data?.receiverAddress?.name) {
      message.error(
        "Please select the warehouse address in order to submit the order",
      );
      return;
    }
    // console.log(data);
    const res = await updateOrder(data);
    // console.log("res----->", res);
  }

  useEffect(() => {
    if (orderDetails) {
      const {
        carrier,
        customer,
        requiredDate,
        shipDate,
        items = [],
        poNumber = "",
        palletCount = 0,
        orderWeight = 0,
        receiverAddress,
      } = orderDetails;
      reset({
        carrier: carrier?._id,
        customer: customer?._id,
        requiredDate: dayjs(requiredDate),
        shipDate: shipDate && dayjs(shipDate),
        items: items?.map(({ product, requiredQuantity }) => ({
          product: product?._id,
          requiredQuantity,
        })),
        poNumber: poNumber,
        palletCount: palletCount,
        orderWeight: orderWeight,
        receiverAddress: receiverAddress,
      });
    }
  }, [orderDetails, reset]);

  useEffect(() => {
    if (binCount && settingDetails?.shipping) {
      const {
        binWeight = BIN_WIEGHT,
        binsPerPallet = BINS_COUNT_PER_PALLET,
        palletWeight = PALLET_WIEGHT,
      } = settingDetails.shipping;
      const palletCount = Math.ceil(binCount / (binsPerPallet || 1));
      const totalWeight = palletCount * palletWeight + binCount * binWeight;
      setValue("palletCount", palletCount);
      setValue("orderWeight", totalWeight);
    } else {
      setValue("palletCount", 0);
      setValue("orderWeight", 0);
    }
  }, [binCount, settingDetails?.shipping]);

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col gap-5 pb-6">
        <div className="flex flex-col gap-6">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
              <Breadcrumb
                items={[
                  {
                    title: <Link to={"/processes/order"}>Orders</Link>,
                  },
                  {
                    title: <div className="text-lg">Order Details</div>,
                  },
                ]}
              />
              <Flex gap={10}>
                {["INITIATED", "APPROVED"].includes(orderDetails?.status) &&
                  ["admin", "OrderCreateAll", "OrderManagement"].some(
                    (p) => userData?.type?.permissions?.[p],
                  ) && (
                    <Popconfirm
                      title="Cancel order"
                      description="Once you click yes, this action can not be undone"
                      onConfirm={async () => {
                        await cancelOrder();
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button htmlType="button" loading={cancelingOrder}>
                        Cancel Order
                      </Button>
                    </Popconfirm>
                  )}
                {(orderDetails?.status === "INITIATED" ||
                  (orderDetails?.type === "OUTBOUND" &&
                    orderDetails?.status === "APPROVED")) && (
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="flex items-center justify-center"
                    loading={updatingOrder}
                  >
                    Update
                  </Button>
                )}
                {orderDetails?.status === "INITIATED" &&
                  ["admin", "OrderCreateAll", "OrderManagement"].some(
                    (p) => userData?.type?.permissions?.[p],
                  ) && (
                    <Button
                      htmlType="button"
                      onClick={async () => {
                        await approveOrder();
                      }}
                      loading={approvingOrder}
                      variant="solid"
                      color="green"
                    >
                      Approve
                    </Button>
                  )}

                {orderDetails?.status === "APPROVED" && (
                  <Button
                    htmlType="button"
                    onClick={async () => {
                      await revokeOrder();
                    }}
                    loading={revokingOrder}
                    variant="solid"
                    color="orange"
                  >
                    Revoke Approval
                  </Button>
                )}
              </Flex>
            </div>
            <div className="px-6 py-6">
              <Row gutter={18}>
                <Col span={12}>
                  <Form.Item label="Order ID" name="referenceId">
                    <Controller
                      control={control}
                      name="referenceId"
                      render={({ field }) => (
                        <Input
                          {...field}
                          disabled
                          value={orderDetails?.referenceId}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Status" name="status">
                    <Controller
                      control={control}
                      name="status"
                      render={({ field }) => (
                        <Input
                          {...field}
                          disabled
                          value={orderDetails?.status}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Customer"
                    name="customer"
                    validateStatus={errors.customer ? "error" : ""}
                    help={errors.customer && errors.customer.message}
                    required={true}
                    wrapperCol={{ sm: 24 }}
                    style={{
                      marginBottom: "8px",
                    }}
                  >
                    <Controller
                      control={control}
                      name="customer"
                      render={({ field }) => {
                        let customer_exists = false;
                        const options = customerList
                          ? customerList.map(({ name, _id }) => {
                              if (field.value === _id) {
                                customer_exists = true;
                              }
                              return {
                                label: name,
                                value: _id,
                              };
                            })
                          : [];
                        if (!customer_exists) {
                          options.push({
                            label: orderDetails?.customer?.name,
                            value: field.value,
                          });
                        }
                        return (
                          <Select
                            {...field}
                            showSearch
                            optionFilterProp="label"
                            placeholder="Customer"
                            allowClear
                            loading={isLoadingCustomerList}
                            options={options}
                            style={{
                              textTransform: "capitalize",
                            }}
                            dropdownStyle={{
                              textTransform: "capitalize",
                            }}
                            disabled={orderDetails?.status !== "INITIATED"}
                          />
                        );
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Carrier"
                    name="carrier"
                    validateStatus={errors.carrier ? "error" : ""}
                    help={errors?.carrier?.message}
                    // required={true}
                    wrapperCol={{ sm: 24 }}
                    style={{
                      marginBottom: "8px",
                    }}
                  >
                    <Controller
                      control={control}
                      name="carrier"
                      render={({ field }) => (
                        <Select
                          {...field}
                          showSearch
                          optionFilterProp="label"
                          placeholder="Carrier"
                          allowClear
                          loading={isLoadingCarrierList}
                          options={carrierList?.map(({ name, _id }) => ({
                            label: name,
                            value: _id,
                          }))}
                          style={{
                            textTransform: "capitalize",
                          }}
                          dropdownStyle={{
                            textTransform: "capitalize",
                          }}
                          disabled={orderDetails?.status !== "INITIATED"}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Delivery Date"
                    name="requiredDate"
                    validateStatus={errors.requiredDate ? "error" : ""}
                    help={errors.requiredDate && errors.requiredDate.message}
                    required={true}
                    wrapperCol={{ sm: 24 }}
                  >
                    <Controller
                      control={control}
                      name="requiredDate"
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          style={{
                            width: "100%",
                          }}
                          disabled={orderDetails?.status !== "INITIATED"}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Ship Date"
                    name="shipDate"
                    validateStatus={errors.shipDate ? "error" : ""}
                    help={errors.shipDate && errors.shipDate.message}
                    wrapperCol={{ sm: 24 }}
                  >
                    <Controller
                      control={control}
                      name="shipDate"
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          // ship date can not be past delivery date.
                          disabledDate={(current) =>
                            watch("requiredDate") &&
                            current.isAfter(watch("requiredDate"))
                          }
                          style={{
                            width: "100%",
                          }}
                          disabled={orderDetails?.status !== "INITIATED"}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Shipped Date"
                    name="shippingDetails[0].shipmentDate"
                  >
                    <Controller
                      control={control}
                      name="shippingDetails[0].shipmentDate"
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          style={{
                            width: "100%",
                          }}
                          disabled={true}
                          placeholder=""
                          value={
                            dayjs(
                              orderDetails?.shippingDetails
                                ?.map((item) => new Date(item?.shipmentDate)) // Convert shipmentDate to Date objects
                                ?.reduce((oldest, current) => {
                                  return current < oldest ? current : oldest;
                                }, new Date()),
                            ) // Find the oldest date,
                          }
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="PO Number"
                    name="poNumber"
                    validateStatus={errors.poNumber ? "error" : ""}
                    help={errors?.poNumber?.message}
                  >
                    <Controller
                      control={control}
                      name="poNumber"
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="PO Number"
                          disabled={orderDetails?.status !== "INITIATED"}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>

                <OrderReceiverAddress
                  errors={errors}
                  control={control}
                  watch={watch}
                  setValue={setValue}
                  orderDetails={orderDetails}
                />
                {orderDetails?.type === "OUTBOUND" &&
                  orderDetails?.status === "APPROVED" && (
                    <>
                      <Col span={12}>
                        <Form.Item
                          label="Driver Name"
                          name="driver.name"
                          validateStatus={errors?.driver?.name ? "error" : ""}
                          help={errors?.driver?.name?.message}
                        >
                          <Controller
                            control={control}
                            name="driver.name"
                            render={({ field }) => (
                              <Input {...field} placeholder="Driver name" />
                            )}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          label="Trailer ID"
                          name="driver.dl"
                          validateStatus={errors?.driver?.dl ? "error" : ""}
                          help={errors?.driver?.dl?.message}
                        >
                          <Controller
                            control={control}
                            name="driver.dl"
                            render={({ field }) => (
                              <Input {...field} placeholder="" />
                            )}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}

                <Col span={24} className="flex justify-between">
                  <p className="text-lg">Items</p>
                  {/* hide Add button (they only have one product at the moment) */}
                  {/* <Button
                    htmlType="button"
                    onClick={() => {
                      append(initialItemDetail);
                    }}
                    disabled={orderDetails?.status !== "INITIATED"}
                  >
                    Add Item
                  </Button> */}
                </Col>
                {fields.map((field, index) => {
                  return (
                    <Col span={24} key={field.id}>
                      <Row gutter={18}>
                        <Col span={12}>
                          <Form.Item
                            label="Product"
                            name={`items.${index}.product`}
                            validateStatus={
                              errors?.items?.[index]?.product ? "error" : ""
                            }
                            help={errors?.items?.[index]?.product?.message}
                            required={true}
                            wrapperCol={{ sm: 24 }}
                            className="flex-grow"
                          >
                            <Controller
                              control={control}
                              name={`items.${index}.product`}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  showSearch
                                  optionFilterProp="label"
                                  placeholder="Product"
                                  allowClear
                                  loading={isLoadingProductList}
                                  options={productList?.map(
                                    ({ name, _id }) => ({
                                      label: name,
                                      value: _id,
                                    }),
                                  )}
                                  style={{
                                    textTransform: "capitalize",
                                  }}
                                  dropdownStyle={{
                                    textTransform: "capitalize",
                                  }}
                                  disabled={
                                    orderDetails?.status !== "INITIATED"
                                  }
                                />
                              )}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            // className="w-full"
                            label="Required Quantity"
                            name={`items.${index}.requiredQuantity`}
                            validateStatus={
                              errors?.items?.[index]?.requiredQuantity
                                ? "error"
                                : ""
                            }
                            help={
                              errors?.items?.[index]?.requiredQuantity?.message
                            }
                            required={true}
                            className="flex-grow"
                          >
                            <Controller
                              control={control}
                              name={`items.${index}.requiredQuantity`}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="number"
                                  min={0}
                                  disabled={
                                    orderDetails?.status !== "INITIATED"
                                  }
                                />
                              )}
                            />
                          </Form.Item>
                        </Col>
                        {/* <Button
                        key={field?.id}
                        onClick={() => {
                          remove(index);
                        }}
                        className="mb-6"
                        disabled={orderDetails?.status !== "INITIATED"}
                      >
                        <DeleteOutlined />
                      </Button> */}
                      </Row>
                    </Col>
                  );
                })}

                <Col span={12}>
                  <Form.Item label="Pallet Count" name="palletCount">
                    <Controller
                      control={control}
                      name="palletCount"
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          min={0}
                          placeholder="0"
                          disabled={true}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Order Weight(lb)" name="orderWeight">
                    <Controller
                      control={control}
                      name="orderWeight"
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          min={0}
                          placeholder="0"
                          step="any"
                          disabled={true}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>

                <OrderAssetDetails
                  orderDetails={orderDetails}
                  isLoading={isLoadingOrderDetails}
                />
                <OrderShippingDetails
                  shippingDetails={orderDetails?.shippingDetails}
                />
              </Row>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default UpdateOrderPage;
